import { z } from 'zod'

import { ensureRouteData } from '@/app/query-client'
import { signalTechnicalsQueryOptions } from '@/features/signals/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import {
  SIGNAL_STOCK_SOURCES,
  prefetchSignalStockFrame,
  signalSymbolOf,
} from '@/screens/signals/SignalStockScreen.loader'

/** The page's three views, `?tab=`: the oscillators, the moving averages, the pivots. */
export const SIGNAL_TECHNICALS_TABS = ['oscillators', 'averages', 'pivots'] as const
export type SignalTechnicalsTab = (typeof SIGNAL_TECHNICALS_TABS)[number]

/**
 * Every field falls back rather than throwing (`screens/signals/search.ts`):
 * `tab` opens on the oscillators, `from` on Live signals, the board the
 * member was sent from and the Signal page carries both ways.
 */
export const signalTechnicalsSearchSchema = z.object({
  tab: z.enum(SIGNAL_TECHNICALS_TABS).default('oscillators').catch('oscillators'),
  from: z.enum(SIGNAL_STOCK_SOURCES).default('live').catch('live'),
})
export type SignalTechnicalsSearch = z.infer<typeof signalTechnicalsSearchSchema>

/**
 * The `/market/signals/stock/$symbol/technicals` loader.
 *
 * MAIN: the stock's technicals (the tables' rows, the gauges and the pivots),
 * whose `null` is the not-found page. SECONDARY, started first: the Signal
 * page's own (`prefetchSignalStockFrame`), so the header and the chart are the
 * same on both pages. An invalid symbol returns before any read.
 */
export async function loadSignalTechnicals({
  context: { queryClient },
  params,
  preload,
}: {
  context: RouterContext
  params: { symbol: string }
  preload: boolean
}): Promise<void> {
  const symbol = signalSymbolOf(params.symbol)
  if (symbol === null) return
  if (!preload) prefetchSignalStockFrame(queryClient, symbol)
  await ensureRouteData(queryClient, signalTechnicalsQueryOptions(symbol))
}
